import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, patch, post } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { Alert, Spinner } from '../lib/ui.js';
import { Badge } from '../lib/components.js';
import { t } from '../i18n/index.js';
import type { Designation } from './WorkerForm.js';

/**
 * The list of things a worker can be.
 *
 * Small on purpose: four seeded values, add more, rename, retire. A designation that has been
 * printed on a document is never deleted — the document said it, so the name has to keep meaning
 * something — which is why "Retire" hides it from the dropdown rather than removing the row.
 */
export function DesignationsCard() {
  const [rows, setRows] = useState<Designation[] | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ designations: Designation[] }>('/admin/designations');
      setRows(r.designations);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/admin/designations', { code: code.trim(), name: name.trim() });
      setCode('');
      setName('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (d: Designation, active: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await patch(`/admin/designations/${d.id}`, { active });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" id="designations">
      <h3>{t('designations_title')}</h3>
      <p className="muted">{t('designations_sub')}</p>
      {error && <Alert>{error}</Alert>}
      {!rows ? (
        <Spinner />
      ) : (
        <ul className="list">
          {rows.map((d) => (
            <li key={d.id} className="row between">
              <span>
                <strong>{d.name}</strong> <span className="muted">({d.code})</span>
                {d.active === false && (
                  <Badge tone="neutral" className="no-ml">
                    {t('designations_retired')}
                  </Badge>
                )}
              </span>
              <button
                type="button"
                className="small secondary"
                disabled={busy}
                onClick={() => void setActive(d, d.active === false)}
              >
                {d.active === false ? t('designations_restore') : t('designations_retire')}
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="grid-form">
        <label>
          {t('designations_code')}
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 12))}
            required
            maxLength={12}
            placeholder="QA"
          />
        </label>
        <label>
          {t('designations_name')}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
            placeholder="Quality Auditor"
          />
        </label>
        <button
          type="submit"
          className="grid-form-action"
          disabled={busy || !code.trim() || !name.trim()}
        >
          {t('designations_add')}
        </button>
      </form>
    </section>
  );
}
